
// clearAll.js - Robust utility to clear all transient UI content

// --- GLOBAL UI STATE MANAGEMENT ---
if (!window.UI_STATE) {
    window.UI_STATE = {
        menuOpen: false,
        popupOpen: false,
        // Add more as needed
    };
}

export function setUIState(key, value) {
    window.UI_STATE[key] = value;
    window.dispatchEvent(new CustomEvent('uiStateChanged', { detail: { key, value } }));
}

export function getUIState(key) {
    return window.UI_STATE[key];
}

import { resetSearchBarContent } from '../header/header-search-bar.js';
import { setFavoritesButtonInactive } from './favorites.js';
import { getMap } from '../map/map.js';
import { hideCardsBanner } from '../cards/index.js';
import { parseUrlForExploreAreaPackTag, parseUrlForDiscover } from './url-utils.js';
import { setHeaderButtonActive } from '../header/header-buttons.js';
import { forceCloseAllPopups } from '../components/Popup.js';

// Helper to fade out and remove an element
function fadeAndRemove(element) {
    if (!element) return;
    element.classList.add('fade-out');
    setTimeout(() => {
        if (element && element.parentNode) {
            element.parentNode.removeChild(element);
        }
    }, 200);
}

// Close Explore popup (if open)
function closeExplorePopup(animate = false) {
    // Handled by generic popup closer now
    // Legacy specific check removed to avoid conflict with reusable popup
}

// Close Favorites overlay (if open)
function closeFavoritesOverlay(animate = false) {
    const bottomButtons = document.querySelector('.bottom-buttons-container');
    if (bottomButtons) bottomButtons.style.display = 'none';
}

// Close any generic popup overlays (from Popup.js)
// Uses forceCloseAllPopups to properly reset the shared overlay state
function closePopupOverlays() {
    forceCloseAllPopups();
}

// Close marker popups
function closeMarkerPopups() {
    try {
        const map = getMap();
        if (map && typeof map.closePopup === 'function') {
            map.closePopup();
        }
    } catch (error) {
        document.querySelectorAll('.marker-popup').forEach(popup => popup.remove());
    }
}

// Clear spot selection (reset map action buttons from spot mode to default mode)
function clearSpotSelection() {
    window.dispatchEvent(new CustomEvent('spotCleared'));
}

// Deselect header buttons
function deselectHeaderButtons() {
    document.querySelectorAll('.header-action-btn.active').forEach(btn => btn.classList.remove('active'));
}

// Uncheck favorites button
function uncheckFavoritesButton() {
    const favBtn = document.querySelector('.favorites-btn, .favorites-button, #favorites-btn, #menu-favorites-btn');
    if (favBtn) favBtn.classList.remove('active');
    if (typeof setFavoritesButtonInactive === 'function') setFavoritesButtonInactive();
}

// Close left menu if open
function closeLeftMenu() {
    // Dispatch event to let header-menu-left.js handle closing and state update
    window.dispatchEvent(new CustomEvent('requestCloseMenu'));
}

// Restore header button based on current URL (explore vs discover/map)
export function restoreActiveFlowFromUrl() {
    try {
        // On direct URL loads (results already shown), no popup is open.
        // Keep header buttons deselected by default.
        setHeaderButtonActive(null);
    } catch (error) {
        // Fail silently to avoid breaking UI cleanup
        console.warn('restoreActiveFlowFromUrl failed:', error);
    }
}

// Main clearAllUI function
export function clearAllUI(options = {}) {
    // keepCards defaults true so cards banner stays mounted when clearing UI
    const { animate = false, keepPopup = false, keepMenu = false, keepCards = true, keepFavorites = false, keepSpot = false } = options;

    // Only close popups if NOT keeping them
    // Special handling: If keepSpot is true, do NOT close marker popups (including image popups)
    if (!keepPopup) {
        // If keepSpot is true, only close generic overlays, not marker popups
        if (keepSpot) {
            // Only close overlays that are NOT marker popups
            // forceCloseAllPopups should be updated to skip marker popups if possible
            // For now, we skip closeMarkerPopups below if keepSpot is true
            closePopupOverlays();
            // Do NOT nullify window.currentPopupInstance if it's a marker popup for the selected spot
            // (Assume marker popup is managed by marker selection state)
        } else {
            closePopupOverlays();
            if (window.currentPopupInstance) {
                window.currentPopupInstance = null;
            }
        }
    }

    // closeExplorePopup(animate); // Removed/Merged
    // closeDiscoverBar(animate); // Removed

    closeFavoritesOverlay(animate);
    // Only close marker popups if NOT keeping spot selection
    if (!keepSpot) {
        closeMarkerPopups();
    }

    // Always clear spot selection unless explicitly told to keep it
    if (!keepSpot) {
        clearSpotSelection();
    }

    deselectHeaderButtons();

    // Only uncheck favorites button if NOT keeping favorites state
    if (!keepFavorites) {
        uncheckFavoritesButton();
    }

    // Only close menu if not keeping it
    if (!keepMenu) {
        closeLeftMenu(); // Close left menu if open
    }

    if (!keepCards && typeof hideCardsBanner === 'function') {
        hideCardsBanner();
    }
    if (typeof resetSearchBarContent === 'function') {
        resetSearchBarContent();
    }

    // Note: restoring active flow is handled when popups/menus close,
    // not automatically here to avoid overriding intentional deselection.
}
